import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Tabs } from '@/components/ui/Tabs';

const jsCode = `import { MemoryGateway } from '@memorygateway/sdk';

const memory = new MemoryGateway({
  apiKey: process.env.MEMORY_GATEWAY_KEY,
  agentId: 'my-agent-001'
});

// Remember context automatically
await memory.remember({
  type: 'task_progress',
  content: 'Completed Phase 2 of database migration'
});

// Recall relevant memories for current task
const memories = await memory.recall({
  query: 'What stage is the database migration in?',
  limit: 5
});`;

const pythonCode = `from memorygateway import MemoryGateway

memory = MemoryGateway(
    api_key=os.environ["MEMORY_GATEWAY_KEY"],
    agent_id="my-agent-001"
)

# Remember context automatically
await memory.remember(
    type="task_progress",
    content="Completed Phase 2 of database migration"
)

# Recall relevant memories for current task
memories = await memory.recall(
    query="What stage is the database migration in?",
    limit=5
)`;

const curlCode = `# Remember context
curl -X POST https://api.memory-gateway.dev/v1/memory \\
  -H "Authorization: Bearer $MEMORY_GATEWAY_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "agentId": "my-agent-001",
    "type": "task_progress",
    "content": "Completed Phase 2 of database migration"
  }'

# Recall relevant memories
curl -X POST https://api.memory-gateway.dev/v1/recall \\
  -H "Authorization: Bearer $MEMORY_GATEWAY_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "agentId": "my-agent-001",
    "query": "What stage is the database migration in?",
    "limit": 5
  }'`;

const CodeBlock: React.FC<{ code: string; language: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <div className="absolute top-3 right-3 z-10">
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-medium rounded-md transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              Copy
            </>
          )}
        </button>
      </div>
      <div className="bg-code-bg rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <span className="text-sm text-slate-400 font-mono">{language}</span>
        </div>
        <div className="p-4 overflow-x-auto">
          <pre className="text-sm font-mono text-slate-300 leading-relaxed">
            {code.split('\n').map((line, i) => (
              <div key={i} className="flex">
                <span className="select-none text-slate-600 w-8 flex-shrink-0 text-right pr-4">
                  {i + 1}
                </span>
                <span>{line}</span>
              </div>
            ))}
          </pre>
        </div>
      </div>
    </div>
  );
};

export const SDKPreview: React.FC = () => {
  return (
    <section className="py-20 bg-surface-secondary">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
            Simple SDK, Powerful Memory
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Drop-in SDK works with your existing agent framework
          </p>
        </div>

        <Tabs
          tabs={[
            {
              label: 'JavaScript',
              panel: <CodeBlock code={jsCode} language="JavaScript" />,
            },
            {
              label: 'Python',
              panel: <CodeBlock code={pythonCode} language="Python" />,
            },
            {
              label: 'cURL',
              panel: <CodeBlock code={curlCode} language="cURL" />,
            },
          ]}
        />
      </div>
    </section>
  );
};

export default SDKPreview;